import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  CalendarClock,
  FileText,
  Settings,
  LifeBuoy,
} from "lucide-react";
import Logo from "@/components/Logo";
import { AGENCY } from "@/data/agency";
import { useAuth } from "@/context/AuthContext";
import SupportModal from "@/components/SupportModal";

const NAV_ITEMS = [
  { to: "/app",           label: "Dashboard",  icon: LayoutDashboard, testId: "nav-dashboard", end: true },
  { to: "/app/dossiers",   label: "Dossiers",   icon: FolderKanban,    testId: "nav-dossiers" },
  { to: "/app/planning",   label: "Planning",   icon: CalendarClock,   testId: "nav-planning" },
  { to: "/app/documents",  label: "Documents",  icon: FileText,        testId: "nav-documents" },
  { to: "/app/parametres", label: "Paramètres", icon: Settings,        testId: "nav-parametres" },
];

const Sidebar = () => {
  const { agency, user, logout } = useAuth();
  const [supportOpen, setSupportOpen] = useState(false);
  const agencyName = agency?.name || AGENCY.name;
  const agencyInitials =
    (agencyName || "")
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || AGENCY.initials;
  const userName = user?.name || AGENCY.user.name;

  return (
    <aside
      data-testid="sidebar"
      className="hidden lg:flex fixed left-0 top-0 h-screen w-72 flex-col p-5 border-r border-[#EEF2F5]"
      style={{
        background:
          "linear-gradient(180deg, #FFFFFF 0%, #F3F5F7 55%, #EEF2F5 100%)",
      }}
    >
      {/* Logo */}
      <div className="px-2 pt-2 pb-8" data-testid="sidebar-logo">
        <Logo variant="full" size="md" rounded="rounded-lg" testId="sidebar-logo-img" />
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-1">
        {NAV_ITEMS.map(({ to, label, icon: Icon, testId, end, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              [
                "group flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[14px] font-medium transition-all duration-200",
                isActive
                  ? "bg-white text-[#111111] shadow-[0_1px_2px_rgba(17,17,17,0.04)] border border-[#EEF2F5]"
                  : "text-[#6B7280] hover:text-[#111111] hover:bg-white/70",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  size={18}
                  strokeWidth={1.75}
                  className={
                    isActive
                      ? "text-[#111111]"
                      : "text-[#9CA3AF] group-hover:text-[#111111]"
                  }
                />
                <span className="flex-1">{label}</span>
                {badge && !isActive && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#111111] text-white text-[10.5px] font-semibold flex items-center justify-center">
                    {badge}
                  </span>
                )}
                {isActive && (
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C8FF2F]" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom */}
      <div className="mt-auto" data-testid="sidebar-footer">
        <div className="rounded-2xl border border-[#EEF2F5] bg-white/80 backdrop-blur p-4">
          <div className="flex items-center gap-2 text-[#111111]">
            <LifeBuoy size={16} strokeWidth={1.75} />
            <span className="text-[13px] font-semibold">Assistance</span>
          </div>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-[#6B7280]">
            Une question sur un dossier&nbsp;? Notre équipe répond en moins de
            2h.
          </p>
          <button
            data-testid="sidebar-contact-btn"
            type="button"
            onClick={() => setSupportOpen(true)}
            className="mt-3 w-full rounded-lg bg-[#111111] text-white text-[12.5px] font-medium py-2 hover:bg-black transition-colors"
          >
            Contacter le support
          </button>
        </div>

        <div className="mt-4 flex items-center gap-3 px-2" data-testid="sidebar-user">
          <div className="h-9 w-9 rounded-xl bg-[#111111] flex items-center justify-center text-[#C8FF2F] text-[12px] font-semibold">
            {agencyInitials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#111111] truncate">
              {agencyName}
            </p>
            <p className="text-[11.5px] text-[#6B7280] truncate">
              {userName}
            </p>
          </div>
          <button
            data-testid="sidebar-logout"
            onClick={logout}
            className="h-7 px-2 text-[10.5px] font-medium text-[#6B7280] hover:text-[#111111] hover:bg-white rounded-md transition-colors"
            title="Se déconnecter"
          >
            Sortir
          </button>
        </div>
      </div>
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </aside>
  );
};

export default Sidebar;
